import {noop} from '@benchling/js-utils';
import {RefCallback} from 'react';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import useResizeObserver from 'use-resize-observer';

/**
 * The type that we return if the environment we're running in doesn't support ResizeObserver. This is
 * typically when we are running within a NodeJS environment which doesn't have a fully functional DOM.
 */
export interface UnsupportedResizeObserver<T extends HTMLElement> {
  ref: RefCallback<T>;
  width?: never;
  height?: never;
}

export type HookResponseOrUnsupported<T extends HTMLElement> =
  | ReturnType<typeof useResizeObserver<T>>
  | UnsupportedResizeObserver<T>;

/**
 *  Thin wrapper around the useResizeObserver package. We install a polyfilled version,
 *  to ensure backwards compatibility. (Specifically: the native version is available
 *  in Safari beginning with 13.1; Benchling supports Safari 11.)
 */
if (!window.ResizeObserver) {
  // @ts-expect-error
  window.ResizeObserver = ResizeObserverPolyfill;
}

/**
 * This hook is a thin wrapper around the useResizeObserver package in most cases. However, we also have to
 * handle when we are running in an environment that doesnt't support ResizeObserver (like NodeJS). In that
 * case, we return an object that has a ref but does not have a width or height. Callers of this function
 * should handle this case and deal with the height or width being null. That's something that they already
 * needed to do but we're explicitly calling it out here.
 */
export default function <T extends HTMLElement>(
  ...opts: Parameters<typeof useResizeObserver<T>>
): HookResponseOrUnsupported<T> {
  if (global.__NODE__) {
    return {
      ref: noop as RefCallback<T>,
    };
  }
  // oxlint-disable-next-line rules-of-hooks
  return useResizeObserver(...opts);
}
